import type { ProjectContextDocument } from '@protoss/shared';
import { ArrowLeftIcon, Button, CheckIcon, IconSize, RichTextEditor } from '@protoss/ui';
import * as React from 'react';
import { useDirtyState } from '../../../hooks/useDirtyState';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';

interface ContextDocEditorProps {
  /** Document being edited, or null for a new document */
  document: ProjectContextDocument | null;
  saving: boolean;
  /** True briefly after a successful save to show "Saved" feedback */
  saveSucceeded?: boolean;
  onSave: (title: string, body: Record<string, unknown> | null, contentText: string) => void;
  onCancel: () => void;
}

export function ContextDocEditor({
  document,
  saving,
  saveSucceeded,
  onSave,
  onCancel,
}: ContextDocEditorProps) {
  const [title, setTitle] = React.useState(document?.title ?? '');
  const [body, setBody] = React.useState<Record<string, unknown> | null>(document?.body ?? null);
  const [contentText, setContentText] = React.useState(document?.contentText ?? '');

  // Reset state when switching documents
  React.useEffect(() => {
    setTitle(document?.title ?? '');
    setBody(document?.body ?? null);
    setContentText(document?.contentText ?? '');
  }, [document?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    title: string;
    body: Record<string, unknown> | null;
  }>({ trimStrings: true });

  const baselinePayload = React.useMemo(
    () => ({
      title: document?.title ?? '',
      body: document?.body ?? null,
    }),
    [document?.body, document?.title, document?.uuid]
  );

  const currentPayload = React.useMemo(
    () => ({
      title,
      body,
    }),
    [body, title]
  );

  React.useEffect(() => {
    setBaseline(baselinePayload);
  }, [baselinePayload, setBaseline]);

  React.useEffect(() => {
    updateCurrent(currentPayload);
  }, [currentPayload, updateCurrent]);

  useUnsavedChanges(!saving && isDirty, 'rr:context-doc');

  const canSave = title.trim().length > 0 && !saving;

  const handleEditorUpdate = React.useCallback((json: Record<string, unknown>, text: string) => {
    setBody(json);
    setContentText(text);
  }, []);

  const handleSave = () => {
    if (!canSave) return;
    onSave(title.trim(), body, contentText);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="ctx-doc-editor" onKeyDown={handleKeyDown}>
      <div className="ctx-doc-editor__title-row">
        <input
          type="text"
          className="ctx-doc-editor__title-input"
          placeholder="Document title…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={250}
          autoFocus
          aria-label="Document title"
        />
      </div>

      <div className="ctx-doc-editor__body">
        <RichTextEditor
          content={body}
          onUpdate={handleEditorUpdate}
          placeholder="Type / to view controls"
          minHeight="240px"
          className="ctx-doc-editor__rte"
          toolbar
        />
      </div>

      <div className="ctx-doc-editor__actions">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={saving}
          className="ctx-doc-editor__back-btn"
        >
          <ArrowLeftIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
          Back
        </Button>
        <div className="ctx-doc-editor__actions-end">
          <span className="ctx-doc-editor__saved-feedback" role="status" aria-live="polite">
            {saveSucceeded ? (
              <>
                <CheckIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
                Saved
              </>
            ) : null}
          </span>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={!canSave || !isDirty}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
